import type { ReactNode } from 'react'

type MfaMethodRowProps = {
    icon: ReactNode
    title: string
    description: string
    status: 'on' | 'off'
    statusLabel: string
    actionLabel: string
    actionTone?: 'default' | 'danger'
    onAction: () => void
    disabled?: boolean
    children?: ReactNode
}

const statusClasses = {
    on: 'bg-account-success-soft text-account-success',
    off: 'bg-account-page text-account-secondary',
} as const

const actionClasses = {
    default: 'border-account-border text-account-primary hover:bg-account-muted',
    danger: 'border-account-border text-account-error hover:bg-account-error-soft',
} as const

export const MfaMethodRow = ({
    icon,
    title,
    description,
    status,
    statusLabel,
    actionLabel,
    actionTone = 'default',
    onAction,
    disabled,
    children,
}: MfaMethodRowProps) => {
    return (
        <div className="border-account-divider border-b p-6 last:border-b-0">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-4">
                    <div className="border-account-divider bg-account-muted text-account-secondary mt-1 rounded-full border p-2.5">
                        {icon}
                    </div>
                    <div>
                        <div className="mb-1 flex items-center gap-2">
                            <h3 className="text-account-strong font-bold">{title}</h3>
                            <span
                                className={`rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${statusClasses[status]}`}
                            >
                                {statusLabel}
                            </span>
                        </div>
                        <p className="text-account-secondary text-sm leading-relaxed">{description}</p>
                    </div>
                </div>

                <button
                    type="button"
                    onClick={onAction}
                    disabled={disabled}
                    className={`bg-account-surface whitespace-nowrap rounded-lg border px-5 py-2 font-semibold transition active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 ${actionClasses[actionTone]}`}
                >
                    {actionLabel}
                </button>
            </div>

            {children && <div className="border-account-divider mt-5 border-t pt-5">{children}</div>}
        </div>
    )
}
